import React from 'react';
import TailwindModal from '../Tailwind/TailwindModal';
import { useErpConnections } from './hooks/useErpConnections';
import ListView from './views/ListView';
import AddView from './views/AddView';
import DetailsView from './views/DetailsView';

interface ErpConnectionsModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    organizationId: number;
    isAdmin?: boolean;
}

const VIEW_TITLES: Record<string, string> = {
    list: 'ERP Connections',
    add: 'Add New Connection',
    details: 'Connection Details',
};

const VIEW_DESCRIPTIONS: Record<string, string> = {
    list: 'Manage your ERP system integrations.',
    add: 'Configure a new ERP connection for your organization.',
    details: 'View and manage connection configuration.',
};

export default function ErpConnectionsModal({
    open,
    setOpen,
    organizationId,
    isAdmin = false,
}: ErpConnectionsModalProps) {
    const {
        // State
        connections,
        loading,
        viewMode,
        selectedConnection,
        selectedConnectionType,
        newConnectionType,
        createLoading,
        isEditing,
        saveLoading,
        saveError,
        saveSuccess,
        testLoading,
        testResult,
        isNewlyCreated,
        deleteLoading,
        showDeleteConfirm,
        privateKeyInputMode,
        salesforceForm,

        // Setters
        setNewConnectionType,
        setIsEditing,
        setSaveError,
        setSaveSuccess,
        setTestResult,
        setShowDeleteConfirm,
        setPrivateKeyInputMode,
        setSalesforceForm,

        // Actions
        resetState,
        handleViewDetails,
        handleAddConnection,
        handleBackToList,
        handleCreateConnection,
        handleSaveConnection,
        handleStartEditing,
        handleTestConnection,
        handleDeleteConnection,
    } = useErpConnections(open, organizationId);

    const handleClose = () => {
        setOpen(false);
        resetState();
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setSaveError(null);
    };

    return (
        <TailwindModal
            isOpen={open}
            onClose={handleClose}
            title={VIEW_TITLES[viewMode]}
            description={VIEW_DESCRIPTIONS[viewMode]}
            size="3xl"
        >
            {viewMode === 'list' && (
                <ListView
                    connections={connections}
                    loading={loading}
                    isAdmin={isAdmin}
                    onAddConnection={handleAddConnection}
                    onViewDetails={handleViewDetails}
                />
            )}

            {viewMode === 'add' && (
                <AddView
                    newConnectionType={newConnectionType}
                    setNewConnectionType={setNewConnectionType}
                    salesforceForm={salesforceForm}
                    setSalesforceForm={setSalesforceForm}
                    privateKeyInputMode={privateKeyInputMode}
                    setPrivateKeyInputMode={setPrivateKeyInputMode}
                    createLoading={createLoading}
                    saveError={saveError}
                    setSaveError={setSaveError}
                    onBack={handleBackToList}
                    onCreate={handleCreateConnection}
                />
            )}

            {viewMode === 'details' && (
                <DetailsView
                    connection={selectedConnection}
                    connectionType={selectedConnectionType}
                    isAdmin={isAdmin}
                    isEditing={isEditing}
                    isNewlyCreated={isNewlyCreated}
                    loading={loading}
                    saveLoading={saveLoading}
                    saveError={saveError}
                    saveSuccess={saveSuccess}
                    testLoading={testLoading}
                    testResult={testResult}
                    deleteLoading={deleteLoading}
                    showDeleteConfirm={showDeleteConfirm}
                    salesforceForm={salesforceForm}
                    setSalesforceForm={setSalesforceForm}
                    privateKeyInputMode={privateKeyInputMode}
                    setPrivateKeyInputMode={setPrivateKeyInputMode}
                    setSaveError={setSaveError}
                    setSaveSuccess={setSaveSuccess}
                    setTestResult={setTestResult}
                    setShowDeleteConfirm={setShowDeleteConfirm}
                    onBack={handleBackToList}
                    onStartEditing={handleStartEditing}
                    onSave={handleSaveConnection}
                    onCancelEdit={handleCancelEdit}
                    onTest={handleTestConnection}
                    onDelete={handleDeleteConnection}
                />
            )}
        </TailwindModal>
    );
}
